import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import './styles.css';
import { DEFAULT_POINTS, frameAtDistance, makeRailGeometry, sampleTrack } from './track.ts';
import { initialState, runDeterministicFixtures, simulateStep, type SimulationState } from './physics.ts';
import { downloadBlob, downloadJson, MetricsCollector } from './metrics.ts';
import { containDestinationRect, coverSourceRect } from './media-fit.ts';
import {
  THEMES,
  applyThemeToAssetKit,
  applyThemeToWorld,
  createSharedAssetKit,
  themeEvidence,
  type ThemeId,
} from './theme.ts';

type Route = 'theme-proof' | 'geometry' | 'physics' | 'editor' | 'ride-perf' | 'media' | 'camera' | 'hero';

interface RouteInfo { label: string; title: string; description: string; checks: string[] }

const ROUTES: Record<Route, RouteInfo> = {
  'theme-proof': { label: 'Theme proof', title: 'Shared assets, selectable environments', description: 'Park Model is the default. Toyworks and Afterglow reparameterize the same geometry and material slots without creating theme-specific models.', checks: ['Switch themes and confirm render counts remain stable', 'Inspect track readability in all three environments', 'Capture the fixed 16:9 and 9:16 proof frames'] },
  geometry: { label: 'Geometry', title: 'Stable track frames', description: 'Parallel-transport orientation frames drive procedural twin rails through hills, turns, and banking.', checks: ['Inspect arrows for abrupt flips', 'Compare minimum adjacent-normal dot', 'Exercise the same frames for car placement'] },
  physics: { label: 'Physics + G', title: 'Track-bound dynamics', description: 'A fixed 120 Hz step applies gravity, drag, lift, and brake sections while sampling local apparent forces.', checks: ['Run deterministic fixtures', 'Observe force telemetry', 'Verify no renderer state enters simulation'] },
  editor: { label: 'Editor', title: 'Desktop control manipulation', description: 'Drag the bright control points in the ground plane. Only the disposable track mesh is rebuilt.', checks: ['Pointer selection is obvious', 'Dragging updates geometry', 'Generation latency is recorded'] },
  'ride-perf': { label: 'Ride performance', title: 'Representative ride load', description: 'A deliberately busy procedural diorama exercises track, supports, scenery, shadows, and chase-camera motion.', checks: ['Collect at least 600 frames', 'Record p50/p95/worst frame times', 'Repeat on a real mobile browser'] },
  media: { label: 'Canvas media', title: 'Replay capture capability', description: 'Records the WebGL canvas with the best MediaRecorder MIME type available in the browser.', checks: ['Record and replay WebM', 'Record codec and byte size', 'Use photo fallback if unsupported'] },
  camera: { label: 'Camera + mic', title: 'Opt-in reaction composite', description: 'Camera and microphone are requested only after an explicit click, composited locally, then stopped immediately.', checks: ['Denial never blocks the lab', 'Separate camera/mic choices', 'Discard and revoke stop all tracks'] },
  hero: { label: 'Hero crops', title: 'Deterministic hero image', description: 'A fixed diorama camera produces repeatable center crops for card, page hero, and vertical sharing formats.', checks: ['Repeat capture after route reload', 'Inspect all aspect crops', 'Confirm track stays legible at thumbnail size'] },
};

const app = document.querySelector<HTMLDivElement>('#app')!;
app.className = 'app';
app.innerHTML = `
  <header><div><div class="eyebrow">Phase 0 feasibility lab</div><h1>Buncha Coasters</h1></div><label class="theme-picker">Environment theme <select aria-label="Environment theme"><option value="park-model">Park Model (default)</option><option value="toyworks">Toyworks</option><option value="afterglow">Afterglow</option></select></label><div class="quarantine">Disposable prototype · not production architecture</div></header>
  <nav aria-label="Experiments"></nav>
  <main class="canvas-wrap"><canvas class="webgl" aria-label="Three-dimensional coaster experiment"></canvas><div class="hud"></div><div class="center-message"></div></main>
  <aside><div class="eyebrow">Active experiment</div><h2></h2><p class="description"></p><div class="controls"></div><div class="route-extra"></div><h3>Live metrics</h3><div class="metrics"></div><h3>Verification</h3><ul class="checks"></ul><div class="status">Ready.</div></aside>`;

const nav = app.querySelector('nav')!;
const canvas = app.querySelector<HTMLCanvasElement>('canvas')!;
const hud = app.querySelector<HTMLDivElement>('.hud')!;
const message = app.querySelector<HTMLDivElement>('.center-message')!;
const title = app.querySelector<HTMLHeadingElement>('aside h2')!;
const description = app.querySelector<HTMLParagraphElement>('.description')!;
const controls = app.querySelector<HTMLDivElement>('.controls')!;
const extra = app.querySelector<HTMLDivElement>('.route-extra')!;
const metricsBox = app.querySelector<HTMLDivElement>('.metrics')!;
const checks = app.querySelector<HTMLUListElement>('.checks')!;
const status = app.querySelector<HTMLDivElement>('.status')!;
const themePicker = app.querySelector<HTMLSelectElement>('.theme-picker select')!;

for (const [id, route] of Object.entries(ROUTES) as [Route, RouteInfo][]) {
  const button = document.createElement('button');
  button.textContent = route.label;
  button.dataset.route = id;
  button.onclick = () => { location.hash = id; };
  nav.append(button);
}

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, preserveDrawingBuffer: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.08;

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 260);
const content = new THREE.Group();
scene.add(content);
const skyMaterial = new THREE.ShaderMaterial({
  side: THREE.BackSide,
  depthWrite: false,
  uniforms: {
    topColor: { value: new THREE.Color(0x72a7cb) },
    horizonColor: { value: new THREE.Color(0xcde8e4) },
  },
  vertexShader: `varying vec3 vWorldPosition; void main() { vec4 worldPosition = modelMatrix * vec4(position, 1.0); vWorldPosition = worldPosition.xyz; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: `uniform vec3 topColor; uniform vec3 horizonColor; varying vec3 vWorldPosition; void main() { float h = clamp(normalize(vWorldPosition).y * 0.72 + 0.38, 0.0, 1.0); float shaped = smoothstep(0.0, 1.0, h); gl_FragColor = vec4(mix(horizonColor, topColor, shaped), 1.0); }`,
});
const sky = new THREE.Mesh(new THREE.SphereGeometry(190, 32, 18), skyMaterial);
sky.renderOrder = -100;
scene.add(sky);
const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
scene.environmentIntensity = 0.3;
pmrem.dispose();
const hemisphere = new THREE.HemisphereLight();
scene.add(hemisphere);
const sun = new THREE.DirectionalLight(0xfff0cf, 3.4);
sun.position.set(25, 48, 18);
sun.castShadow = true;
sun.shadow.mapSize.set(innerWidth < 900 ? 1024 : 2048, innerWidth < 900 ? 1024 : 2048);
sun.shadow.camera.left = sun.shadow.camera.bottom = -50;
sun.shadow.camera.right = sun.shadow.camera.top = 50;
sun.shadow.camera.near = 1;
sun.shadow.camera.far = 110;
sun.shadow.bias = -0.00035;
sun.shadow.normalBias = 0.035;
sun.shadow.radius = 2.4;
scene.add(sun);
const fill = new THREE.DirectionalLight(0x9fc7db, 0.85);
fill.position.set(-32, 22, -28);
scene.add(fill);
const stationPracticals = [new THREE.PointLight(), new THREE.PointLight()];
stationPracticals[0].position.set(-11, 3.8, 12);
stationPracticals[1].position.set(-4, 3.8, 12);
stationPracticals.forEach((light) => {
  light.distance = 14;
  light.decay = 2;
  scene.add(light);
});

const metrics = new MetricsCollector();
const assetKit = createSharedAssetKit();
const trackPoints = DEFAULT_POINTS.map((point) => point.clone());
let track = sampleTrack(trackPoints);
let sharedTrackGeometry = makeRailGeometry(track);
const fixtures = runDeterministicFixtures(track);
let activeRoute: Route = 'theme-proof';
let activeTheme: ThemeId = 'park-model';
let simulation: SimulationState = initialState();
let car: THREE.Mesh | null = null;
let accumulator = 0;
let elapsed = 0;
let routeCleanup: (() => void) | null = null;
let lastMetricsRender = 0;
let rebuildLatency = 0;
let orbitAngle = 0.5;

function setStatus(text: string, kind: 'ok' | 'warn' = 'ok'): void {
  status.textContent = text;
  status.className = `status ${kind}`;
}

function applyActiveTheme(): void {
  const theme = THEMES[activeTheme];
  applyThemeToAssetKit(assetKit, theme);
  applyThemeToWorld(theme, scene, renderer, { hemisphere, sun, fill, practicals: stationPracticals, skyMaterial });
  themePicker.value = activeTheme;
  document.documentElement.style.setProperty('--environment-label', `'${theme.label}'`);
}

function clearContent(): void {
  routeCleanup?.();
  routeCleanup = null;
  content.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (mesh.geometry && !assetKit.sharedGeometries.has(mesh.geometry) && mesh.geometry !== sharedTrackGeometry) mesh.geometry.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    materials.filter(Boolean).forEach((material) => {
      if (!assetKit.sharedMaterials.has(material)) material.dispose();
    });
  });
  content.clear();
  extra.innerHTML = '';
  controls.innerHTML = '';
  message.textContent = '';
  car = null;
  simulation = initialState();
  accumulator = 0;
}

function addGround(): void {
  const base = new THREE.Mesh(assetKit.geometries.base, assetKit.materials.base);
  base.position.y = -2.05;
  base.castShadow = true;
  base.receiveShadow = true;
  const ground = new THREE.Mesh(assetKit.geometries.ground, assetKit.materials.ground);
  ground.position.y = -0.25;
  ground.receiveShadow = true;
  const columns = 28;
  const rows = 24;
  const tiles = new THREE.InstancedMesh(assetKit.geometries.paving, assetKit.materials.paving, columns * rows);
  const tile = new THREE.Object3D();
  let tileIndex = 0;
  for (let column = 0; column < columns; column += 1) {
    for (let row = 0; row < rows; row += 1) {
      tile.position.set((column - (columns - 1) / 2) * 2.05, 0.25, (row - (rows - 1) / 2) * 2.05);
      tile.rotation.y = ((column + row) % 2) * Math.PI * 0.5;
      tile.updateMatrix();
      tiles.setMatrixAt(tileIndex++, tile.matrix);
    }
  }
  tiles.receiveShadow = true;
  tiles.instanceMatrix.needsUpdate = true;
  content.add(base, ground, tiles);
}

interface TrackVisual { mesh: THREE.Mesh; ties: THREE.InstancedMesh }

function updateTies(ties: THREE.InstancedMesh): void {
  const matrix = new THREE.Matrix4();
  ties.count = Math.floor(track.frames.length / 4);
  for (let i = 0; i < ties.count; i += 1) {
    const frame = track.frames[i * 4];
    matrix.makeBasis(frame.binormal, frame.normal, frame.tangent);
    matrix.setPosition(frame.point);
    ties.setMatrixAt(i, matrix);
  }
  ties.instanceMatrix.needsUpdate = true;
  ties.computeBoundingSphere();
}

function addTrack(showFrames = false): TrackVisual {
  const mesh = new THREE.Mesh(sharedTrackGeometry, assetKit.materials.track);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  content.add(mesh);
  const ties = new THREE.InstancedMesh(assetKit.geometries.tie, assetKit.materials.tie, Math.floor(track.frames.length / 4));
  ties.castShadow = true;
  ties.receiveShadow = true;
  updateTies(ties);
  content.add(ties);
  if (showFrames) {
    for (let i = 0; i < track.frames.length; i += 16) {
      const frame = track.frames[i];
      content.add(new THREE.ArrowHelper(frame.normal, frame.point, 2.2, 0x6ce5ff, 0.35, 0.2));
      content.add(new THREE.ArrowHelper(frame.binormal, frame.point, 1.7, 0xffd45f, 0.3, 0.18));
    }
  }
  return { mesh, ties };
}

function addCar(): THREE.Mesh {
  const mesh = new THREE.Mesh(assetKit.geometries.train, assetKit.materials.train);
  mesh.castShadow = true;
  const seatPositions = [-0.66, 0.38];
  seatPositions.forEach((z) => {
    const seat = new THREE.Mesh(assetKit.geometries.trainSeat, assetKit.materials.stationDark);
    seat.position.set(0, 0.6, z);
    seat.castShadow = true;
    mesh.add(seat);
  });
  [[-0.83, -0.35, -0.72], [0.83, -0.35, -0.72], [-0.83, -0.35, 0.78], [0.83, -0.35, 0.78]].forEach(([x, y, z]) => {
    const wheel = new THREE.Mesh(assetKit.geometries.trainWheel, assetKit.materials.stationDark);
    wheel.position.set(x, y, z);
    wheel.rotation.z = Math.PI / 2;
    mesh.add(wheel);
  });
  content.add(mesh);
  car = mesh;
  return mesh;
}

function placeCar(frame = frameAtDistance(track, simulation.distance)): void {
  if (!car) return;
  car.position.copy(frame.point).addScaledVector(frame.normal, 0.75);
  car.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(frame.binormal, frame.normal, frame.tangent));
}

function addScenery(): void {
  const supports = new THREE.InstancedMesh(assetKit.geometries.support, assetKit.materials.support, 90);
  const footings = new THREE.InstancedMesh(assetKit.geometries.supportFooting, assetKit.materials.support, 90);
  const caps = new THREE.InstancedMesh(assetKit.geometries.supportCap, assetKit.materials.support, 90);
  let count = 0;
  const dummy = new THREE.Object3D();
  const basis = new THREE.Matrix4();
  for (let i = 0; i < track.frames.length && count < supports.count; i += 6) {
    const frame = track.frames[i];
    const point = frame.point;
    const height = Math.max(0.8, point.y - 0.05);
    dummy.position.set(point.x, height / 2 + 0.05, point.z);
    dummy.scale.set(1, height, 1);
    dummy.quaternion.identity();
    dummy.updateMatrix();
    supports.setMatrixAt(count++, dummy.matrix);
    dummy.position.set(point.x, 0.18, point.z);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    footings.setMatrixAt(count - 1, dummy.matrix);
    basis.makeBasis(frame.binormal, frame.normal, frame.tangent);
    dummy.position.copy(point).addScaledVector(frame.normal, -0.32);
    dummy.quaternion.setFromRotationMatrix(basis);
    dummy.updateMatrix();
    caps.setMatrixAt(count - 1, dummy.matrix);
  }
  supports.count = count;
  footings.count = count;
  caps.count = count;
  supports.castShadow = true;
  footings.castShadow = true;
  footings.receiveShadow = true;
  caps.castShadow = true;
  supports.instanceMatrix.needsUpdate = true;
  footings.instanceMatrix.needsUpdate = true;
  caps.instanceMatrix.needsUpdate = true;
  content.add(supports, footings, caps);

  const station = new THREE.Group();
  station.name = 'shared-station-asset';
  const foundation = new THREE.Mesh(assetKit.geometries.station, assetKit.materials.stationDark);
  foundation.position.set(-7.5, 0.35, 12);
  foundation.scale.set(11.2, 0.6, 5.6);
  foundation.castShadow = true;
  foundation.receiveShadow = true;
  const stationBase = new THREE.Mesh(assetKit.geometries.station, assetKit.materials.station);
  stationBase.position.set(-7.5, 2.05, 12);
  stationBase.scale.set(8.8, 3.2, 4.6);
  stationBase.castShadow = true;
  stationBase.receiveShadow = true;
  const stationRoof = new THREE.Mesh(assetKit.geometries.stationRoof, assetKit.materials.stationRoof);
  stationRoof.position.set(-7.5, 4, 12);
  stationRoof.scale.set(10.2, 0.42, 5.7);
  stationRoof.castShadow = true;
  const canopy = new THREE.Mesh(assetKit.geometries.stationRoof, assetKit.materials.stationRoof);
  canopy.position.set(-7.5, 2.8, 9.25);
  canopy.scale.set(11.2, 0.22, 1.35);
  canopy.castShadow = true;
  station.add(foundation, stationBase, stationRoof, canopy);
  for (let i = 0; i < 6; i += 1) {
    const column = new THREE.Mesh(assetKit.geometries.stationColumn, assetKit.materials.stationDark);
    column.position.set(-12 + i * 1.8, 1.55, 9.15);
    column.scale.set(1, 2.5, 1);
    column.castShadow = true;
    station.add(column);
  }
  for (let i = 0; i < 3; i += 1) {
    const window = new THREE.Mesh(assetKit.geometries.station, assetKit.materials.stationDark);
    window.position.set(-10.4 + i * 2.9, 2.15, 9.65);
    window.scale.set(1.4, 1.45, 0.08);
    station.add(window);
  }
  const bulbGeometry = new THREE.SphereGeometry(0.16, 10, 8);
  [-11, -8.5, -6, -3.8].forEach((x) => {
    const bulb = new THREE.Mesh(bulbGeometry, assetKit.materials.practical);
    bulb.position.set(x, 3.05, 9.05);
    station.add(bulb);
  });
  content.add(station);

  const treeCount = 150;
  const roundTreeCount = 52;
  const trees = new THREE.InstancedMesh(assetKit.geometries.tree, assetKit.materials.scenery, treeCount);
  const roundTrees = new THREE.InstancedMesh(assetKit.geometries.treeRound, assetKit.materials.hedge, roundTreeCount);
  const trunks = new THREE.InstancedMesh(assetKit.geometries.trunk, assetKit.materials.trunk, treeCount + roundTreeCount);
  const seeded = (index: number) => ((Math.sin(index * 927.17) * 43758.5453) % 1 + 1) % 1;
  for (let i = 0; i < trees.count; i += 1) {
    const angle = seeded(i) * Math.PI * 2;
    const radius = 22 + seeded(i + 1000) * 8.5;
    dummy.position.set(Math.cos(angle) * radius, 1.3, Math.sin(angle) * radius);
    const scale = 0.62 + seeded(i + 2000) * 0.8;
    dummy.scale.setScalar(scale);
    dummy.rotation.set(0, seeded(i + 3000) * Math.PI, 0);
    dummy.updateMatrix();
    trees.setMatrixAt(i, dummy.matrix);
    dummy.position.y = 0.55;
    dummy.scale.set(scale, 1.6 * scale, scale);
    dummy.updateMatrix();
    trunks.setMatrixAt(i, dummy.matrix);
  }
  for (let i = 0; i < roundTrees.count; i += 1) {
    const angle = seeded(i + 5000) * Math.PI * 2;
    const radius = 18 + seeded(i + 6000) * 12;
    const scale = 0.65 + seeded(i + 7000) * 0.9;
    dummy.position.set(Math.cos(angle) * radius, 1.4 * scale, Math.sin(angle) * radius);
    dummy.scale.set(scale, 1.25 * scale, scale);
    dummy.rotation.set(0, seeded(i + 8000) * Math.PI, 0);
    dummy.updateMatrix();
    roundTrees.setMatrixAt(i, dummy.matrix);
    dummy.position.y = 0.55;
    dummy.scale.set(scale, 1.7 * scale, scale);
    dummy.updateMatrix();
    trunks.setMatrixAt(treeCount + i, dummy.matrix);
  }
  const shrubs = new THREE.InstancedMesh(assetKit.geometries.shrub, assetKit.materials.hedge, 170);
  for (let i = 0; i < shrubs.count; i += 1) {
    const angle = seeded(i + 9000) * Math.PI * 2;
    const radius = 12 + seeded(i + 10_000) * 19;
    const scale = 0.45 + seeded(i + 11_000) * 0.85;
    dummy.position.set(Math.cos(angle) * radius, 0.58 * scale, Math.sin(angle) * radius);
    dummy.scale.set(scale, 0.75 * scale, scale);
    dummy.rotation.y = seeded(i + 12_000) * Math.PI;
    dummy.updateMatrix();
    shrubs.setMatrixAt(i, dummy.matrix);
  }
  const rocks = new THREE.InstancedMesh(assetKit.geometries.rock, assetKit.materials.rock, 44);
  for (let i = 0; i < rocks.count; i += 1) {
    const angle = seeded(i + 13_000) * Math.PI * 2;
    const radius = 13 + seeded(i + 14_000) * 17;
    const scale = 0.55 + seeded(i + 15_000) * 0.8;
    dummy.position.set(Math.cos(angle) * radius, 0.55 * scale, Math.sin(angle) * radius);
    dummy.scale.set(scale, 0.72 * scale, scale);
    dummy.rotation.set(seeded(i) * 0.3, seeded(i + 16_000) * Math.PI, seeded(i + 1) * 0.2);
    dummy.updateMatrix();
    rocks.setMatrixAt(i, dummy.matrix);
  }
  const hedges = new THREE.InstancedMesh(assetKit.geometries.hedge, assetKit.materials.hedge, 64);
  let hedgeIndex = 0;
  for (let i = 0; i < 16; i += 1) {
    for (const z of [-28, 28]) {
      dummy.position.set(-30 + i * 4, 0.85, z);
      dummy.scale.set(3.5, 1.35, 0.85);
      dummy.rotation.set(0, 0, 0);
      dummy.updateMatrix();
      hedges.setMatrixAt(hedgeIndex++, dummy.matrix);
    }
    for (const x of [-32, 32]) {
      dummy.position.set(x, 0.85, -26 + i * 3.5);
      dummy.scale.set(0.85, 1.35, 3);
      dummy.updateMatrix();
      hedges.setMatrixAt(hedgeIndex++, dummy.matrix);
    }
  }
  [trees, roundTrees, trunks, shrubs, rocks, hedges].forEach((mesh) => {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.instanceMatrix.needsUpdate = true;
  });
  content.add(trunks, trees, roundTrees, shrubs, rocks, hedges);
}

function button(label: string, action: () => void, primary = false): HTMLButtonElement {
  const element = document.createElement('button');
  element.textContent = label;
  if (primary) element.classList.add('primary');
  element.onclick = action;
  controls.append(element);
  return element;
}

async function captureFixedRender(width: number, height: number, label: string): Promise<void> {
  const parent = canvas.parentElement!;
  const displayWidth = parent.clientWidth;
  const displayHeight = parent.clientHeight;
  const pixelRatio = renderer.getPixelRatio();
  const cameraAspect = camera.aspect;
  try {
    renderer.setPixelRatio(1);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => result ? resolve(result) : reject(new Error('PNG capture returned no data.')), 'image/png');
    });
    downloadBlob(blob, `buncha-coasters-${activeTheme}-${label}.png`);
    setStatus(`Captured deterministic ${width}×${height} ${THEMES[activeTheme].label} proof frame.`);
  } catch (error) {
    setStatus(`Capture failed safely: ${String(error)}`, 'warn');
  } finally {
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(displayWidth, displayHeight, false);
    camera.aspect = cameraAspect;
    camera.updateProjectionMatrix();
  }
}

function updateThemeProofCopy(): void {
  const descriptionElement = extra.querySelector<HTMLElement>('[data-theme-description]');
  if (descriptionElement) descriptionElement.textContent = THEMES[activeTheme].description;
}

function configureThemeProof(): void {
  addGround();
  addTrack();
  addScenery();
  addCar();
  simulation.distance = track.length * 0.39;
  placeCar();
  camera.position.set(38, 30, 40);
  camera.lookAt(0, 7, 2);
  extra.innerHTML = `
    <div class="theme-proof-note" data-theme-description></div>
    <div class="metric"><span>Core geometry sets</span><strong>${Object.keys(assetKit.geometries).length} shared + 1 procedural track</strong></div>
    <div class="metric"><span>Material slots</span><strong>${Object.keys(assetKit.materials).length} shared instances</strong></div>
    <div class="metric"><span>Theme-specific geometry</span><strong>0</strong></div>
    <div class="metric"><span>Render pipeline</span><strong>ACES + PMREM + tuned shadows</strong></div>
    <div class="metric"><span>Screen-space post stack</span><strong>none</strong></div>`;
  updateThemeProofCopy();
  button('Capture 16:9 proof', () => { void captureFixedRender(1600, 900, 'theme-proof-16x9'); }, true);
  button('Capture 9:16 proof', () => { void captureFixedRender(1080, 1920, 'theme-proof-9x16'); });
  setStatus('Park Model is the default environment. Theme changes mutate shared material and lighting parameters in place; no core geometry is replaced.');
}

function configureGeometry(): void {
  addGround();
  addTrack(true);
  addCar();
  camera.position.set(38, 29, 41);
  camera.lookAt(0, 7, 2);
  const dots = track.frames.slice(1).map((frame, index) => frame.normal.dot(track.frames[index].normal));
  const minimumDot = Math.min(...dots);
  message.textContent = `Minimum adjacent normal dot: ${minimumDot.toFixed(5)} · closer to 1 is smoother`;
  setStatus(minimumDot > 0.9 ? 'PASS: no adjacent orientation-frame flip detected.' : 'REVIEW: abrupt frame change detected.', minimumDot > 0.9 ? 'ok' : 'warn');
}

function configurePhysics(performanceLoad = false): void {
  addGround();
  addTrack();
  if (performanceLoad) addScenery();
  addCar();
  camera.position.set(34, 26, 38);
  camera.lookAt(0, 7, 2);
  button('Reset simulation', () => { simulation = initialState(); });
  const failed = fixtures.filter((fixture) => !fixture.passed);
  setStatus(`${fixtures.length - failed.length}/${fixtures.length} deterministic fixtures pass.${failed.length ? `\nFailed: ${failed.map((item) => item.name).join(', ')}` : ''}`, failed.length ? 'warn' : 'ok');
  extra.innerHTML = fixtures.map((fixture) => `<div class="metric"><span>${fixture.passed ? '✓' : '✕'} ${fixture.name}</span><strong>${fixture.actual}</strong></div>`).join('');
}

function configureEditor(): void {
  addGround();
  const trackVisual = addTrack();
  const handles = new THREE.Group();
  const handleGeometry = new THREE.SphereGeometry(0.7, 16, 10);
  const materials = trackPoints.map(() => new THREE.MeshStandardMaterial({ color: 0x72e1ff, emissive: 0x123c4c, roughness: 0.25 }));
  trackPoints.forEach((point, index) => {
    const handle = new THREE.Mesh(handleGeometry, materials[index]);
    handle.position.copy(point);
    handle.userData.index = index;
    handles.add(handle);
  });
  content.add(handles);
  camera.position.set(0, 46, 46);
  camera.lookAt(0, 5, 2);

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0));
  let selected: THREE.Mesh | null = null;
  const onDown = (event: PointerEvent) => {
    const bounds = canvas.getBoundingClientRect();
    pointer.set(((event.clientX - bounds.left) / bounds.width) * 2 - 1, -((event.clientY - bounds.top) / bounds.height) * 2 + 1);
    raycaster.setFromCamera(pointer, camera);
    selected = raycaster.intersectObjects(handles.children)[0]?.object as THREE.Mesh | null;
    if (selected) {
      canvas.setPointerCapture(event.pointerId);
      dragPlane.constant = -selected.position.y;
      (selected.material as THREE.MeshStandardMaterial).emissive.setHex(0x2aa9c3);
    }
  };
  const onMove = (event: PointerEvent) => {
    if (!selected) return;
    const bounds = canvas.getBoundingClientRect();
    pointer.set(((event.clientX - bounds.left) / bounds.width) * 2 - 1, -((event.clientY - bounds.top) / bounds.height) * 2 + 1);
    raycaster.setFromCamera(pointer, camera);
    const hit = new THREE.Vector3();
    if (!raycaster.ray.intersectPlane(dragPlane, hit)) return;
    const started = performance.now();
    selected.position.x = hit.x;
    selected.position.z = hit.z;
    trackPoints[selected.userData.index].copy(selected.position);
    track = sampleTrack(trackPoints);
    const previousTrackGeometry = sharedTrackGeometry;
    sharedTrackGeometry = makeRailGeometry(track);
    trackVisual.mesh.geometry = sharedTrackGeometry;
    updateTies(trackVisual.ties);
    previousTrackGeometry.dispose();
    rebuildLatency = performance.now() - started;
    message.textContent = `Last changed-section rebuild: ${rebuildLatency.toFixed(2)} ms`;
  };
  const onUp = () => {
    if (selected) (selected.material as THREE.MeshStandardMaterial).emissive.setHex(0x123c4c);
    selected = null;
  };
  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  routeCleanup = () => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
  };
  setStatus('Drag a cyan point across the ground plane. Vertical manipulation is intentionally deferred.');
}

function chooseMimeType(): string {
  if (!('MediaRecorder' in window)) return '';
  const appleDevice = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const mp4 = ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4;codecs=avc1.42000a', 'video/mp4'];
  const webm = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp8', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp9', 'video/webm'];
  // Apple devices prefer MP4/H.264 for Files, Quick Look, and share-sheet compatibility.
  // Other browsers retain WebM first because it is broadly supported by canvas capture.
  return (appleDevice ? [...mp4, ...webm] : [...webm, ...mp4])
    .find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
}

interface RecordedMedia { blob: Blob; mimeType: string; filename: string }

async function recordStream(stream: MediaStream, durationMs: number, filenameBase: string): Promise<RecordedMedia> {
  const mimeType = chooseMimeType();
  if (!mimeType) throw new Error('No supported MediaRecorder video MIME type.');
  const chunks: BlobPart[] = [];
  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5_000_000 });
  recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
  const completion = new Promise<void>((resolve) => { recorder.onstop = () => resolve(); });
  recorder.start(250);
  await new Promise((resolve) => setTimeout(resolve, durationMs));
  recorder.requestData();
  await new Promise((resolve) => setTimeout(resolve, 100));
  recorder.stop();
  await completion;
  const actualMimeType = recorder.mimeType || mimeType;
  const blob = new Blob(chunks, { type: actualMimeType });
  if (!blob.size) throw new Error(`MediaRecorder produced an empty ${actualMimeType} recording.`);
  const extension = actualMimeType.includes('mp4') ? 'mp4' : 'webm';
  const filename = `${filenameBase}.${extension}`;
  metrics.media = { ...metrics.media, mimeType: actualMimeType, durationMs, outputBytes: blob.size, recordingSucceeded: true };
  downloadBlob(blob, filename);
  return { blob, mimeType: actualMimeType, filename };
}

function showMediaReview(host: HTMLElement, recording: RecordedMedia, urls: Set<string>, label: string): void {
  host.replaceChildren();
  const url = URL.createObjectURL(recording.blob);
  urls.add(url);
  const heading = document.createElement('strong');
  heading.textContent = `${label} · ${(recording.blob.size / 1024).toFixed(0)} KB`;
  const playback = document.createElement('video');
  playback.controls = true;
  playback.playsInline = true;
  playback.preload = 'metadata';
  playback.src = url;
  playback.addEventListener('loadeddata', () => {
    metrics.media = { ...metrics.media, inPagePlaybackReady: true };
  }, { once: true });
  playback.addEventListener('error', () => {
    metrics.media = { ...metrics.media, inPagePlaybackReady: false, playbackError: playback.error?.message ?? 'unknown' };
  }, { once: true });
  const download = document.createElement('a');
  download.className = 'download-link';
  download.href = url;
  download.download = recording.filename;
  download.textContent = `Download ${recording.filename.endsWith('.mp4') ? 'MP4' : 'WebM'}`;
  const note = document.createElement('p');
  note.textContent = 'Replay here first. On iPhone, choose Download in the system sheet; View may not preview browser-generated MP4 or WebM files.';
  host.className = 'media-review';
  host.append(heading, playback, download, note);
}

function configureMedia(): void {
  configurePhysics(false);
  const reviewUrls = new Set<string>();
  const reviewHost = document.createElement('div');
  extra.prepend(reviewHost);
  const supported = 'captureStream' in canvas && 'MediaRecorder' in window;
  button('Record 3 second canvas replay', async () => {
    const relay = document.createElement('canvas');
    relay.width = 1280;
    relay.height = 720;
    const relayContext = relay.getContext('2d')!;
    const relayBackground = relayContext.createLinearGradient(0, 0, relay.width, relay.height);
    relayBackground.addColorStop(0, '#0c111a');
    relayBackground.addColorStop(1, '#10253a');
    let relayAnimation = 0;
    const relayFrame = () => {
      relayContext.fillStyle = relayBackground;
      relayContext.fillRect(0, 0, relay.width, relay.height);
      containedCanvas(relayContext, canvas, { x: 0, y: 0, width: relay.width, height: relay.height });
      relayAnimation = requestAnimationFrame(relayFrame);
    };
    try {
      setStatus('Recording locally…');
      relayFrame();
      metrics.media = { ...metrics.media, canvasCaptureRelay: true };
      const recording = await recordStream(relay.captureStream(30), 3000, 'buncha-coasters-canvas-spike');
      showMediaReview(reviewHost, recording, reviewUrls, 'Canvas replay ready');
      setStatus(`PASS: replay recorded as ${metrics.media.mimeType}. Replay it below before using the downloaded file.`);
    } catch (error) {
      metrics.media = { ...metrics.media, recordingSucceeded: false };
      setStatus(`FALLBACK REQUIRED: ${String(error)}`, 'warn');
    } finally {
      cancelAnimationFrame(relayAnimation);
    }
  }, true);
  routeCleanup = () => reviewUrls.forEach((url) => URL.revokeObjectURL(url));
  setStatus(supported ? `MediaRecorder available. Preferred type: ${chooseMimeType()}` : 'Canvas recording unsupported; static capture fallback required.', supported ? 'ok' : 'warn');
}

function roundedVideo(context: CanvasRenderingContext2D, video: HTMLVideoElement, x: number, y: number, width: number, height: number): void {
  const source = coverSourceRect(video.videoWidth, video.videoHeight, width, height);
  context.save();
  context.beginPath();
  context.roundRect(x, y, width, height, 28);
  context.clip();
  context.translate(x + width, y);
  context.scale(-1, 1);
  context.drawImage(video, source.x, source.y, source.width, source.height, 0, 0, width, height);
  context.restore();
}

function containedCanvas(
  context: CanvasRenderingContext2D,
  source: HTMLCanvasElement,
  destination: { x: number; y: number; width: number; height: number },
): void {
  const fitted = containDestinationRect(source.width, source.height, destination);
  context.drawImage(source, fitted.x, fitted.y, fitted.width, fitted.height);
}

function configureCamera(): void {
  configurePhysics(false);
  extra.innerHTML = `<div class="status warn">Reaction Cam is optional. Processed on this device; nothing uploads. Camera and microphone stop when you press Stop & discard or leave this experiment.</div><label><input class="mic" type="checkbox" /> Include microphone</label><div class="camera-state">Camera preview is off.</div><div class="camera-review"></div>`;
  const mic = extra.querySelector<HTMLInputElement>('.mic')!;
  const cameraState = extra.querySelector<HTMLElement>('.camera-state')!;
  const reviewHost = extra.querySelector<HTMLElement>('.camera-review')!;
  const preview = document.createElement('div');
  preview.className = 'reaction-live';
  preview.innerHTML = `<span><i></i> LOCAL CAMERA PREVIEW</span><video muted autoplay playsinline></video>`;
  canvas.parentElement!.append(preview);
  const video = preview.querySelector<HTMLVideoElement>('video')!;
  const reviewUrls = new Set<string>();
  let userStream: MediaStream | null = null;
  let compositeAnimation = 0;
  const releaseMedia = () => {
    cancelAnimationFrame(compositeAnimation);
    userStream?.getTracks().forEach((mediaTrack) => mediaTrack.stop());
    userStream = null;
    video.srcObject = null;
    preview.classList.remove('active', 'recording');
    cameraState.textContent = 'Camera preview is off.';
  };
  const stopMedia = () => {
    releaseMedia();
    setStatus('Camera and microphone stopped; local preview discarded.');
  };
  const enable = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('Camera APIs unavailable. Riding and canvas sharing remain available.', 'warn');
      return;
    }
    try {
      userStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: mic.checked });
      video.srcObject = userStream;
      await video.play();
      preview.classList.add('active');
      cameraState.textContent = mic.checked ? 'Live camera preview · microphone on' : 'Live camera preview · microphone off';
      metrics.media = { ...metrics.media, cameraGranted: true, microphoneRequested: mic.checked };
      setStatus('Camera active locally. The preview should be visible over the ride. Press Record to create a 3 second composite.');
    } catch (error) {
      metrics.media = { ...metrics.media, cameraGranted: false, microphoneRequested: mic.checked };
      setStatus(`Permission unavailable or denied. Ordinary riding remains available.\n${String(error)}`, 'warn');
    }
  };
  button('Enable camera', enable, true);
  button('Record 3 second composite', async () => {
    if (!userStream) { setStatus('Enable the camera first. No permission was requested.', 'warn'); return; }
    const composite = document.createElement('canvas');
    composite.width = 720;
    composite.height = 1280;
    const context = composite.getContext('2d')!;
    const draw = () => {
      const background = context.createLinearGradient(0, 0, 0, composite.height);
      background.addColorStop(0, '#0c111a');
      background.addColorStop(0.55, '#10253a');
      background.addColorStop(1, '#0c111a');
      context.fillStyle = background;
      context.fillRect(0, 0, composite.width, composite.height);
      containedCanvas(context, canvas, { x: 0, y: 0, width: composite.width, height: 1190 });
      roundedVideo(context, video, 380, 30, 310, 230);
      context.fillStyle = '#ffffff';
      context.font = '700 22px system-ui';
      context.fillText('BUNCHA COASTERS · REACTION CAM', 24, 1240);
      compositeAnimation = requestAnimationFrame(draw);
    };
    const canvasFit = containDestinationRect(canvas.width, canvas.height, { x: 0, y: 0, width: composite.width, height: 1190 });
    const cameraFit = coverSourceRect(video.videoWidth, video.videoHeight, 310, 230);
    metrics.media = {
      ...metrics.media,
      compositeFit: 'ride-contain-camera-cover',
      rideSourceAspect: Number((canvas.width / canvas.height).toFixed(4)),
      rideOutputAspect: Number((canvasFit.width / canvasFit.height).toFixed(4)),
      cameraSourceAspect: Number((video.videoWidth / video.videoHeight).toFixed(4)),
      cameraCropAspect: Number((cameraFit.width / cameraFit.height).toFixed(4)),
    };
    draw();
    const output = composite.captureStream(30);
    userStream.getAudioTracks().forEach((audioTrack) => output.addTrack(audioTrack));
    try {
      preview.classList.add('recording');
      setStatus('Recording composite locally…');
      const recording = await recordStream(output, 3000, 'buncha-coasters-reaction-spike');
      showMediaReview(reviewHost, recording, reviewUrls, 'Reaction composite ready');
      setStatus('PASS: local ride + reaction composite recorded. Replay it below; camera and microphone are now stopped.');
    } catch (error) {
      setStatus(`Composite failed safely: ${String(error)}`, 'warn');
    } finally {
      releaseMedia();
    }
  });
  const stop = button('Stop & discard', stopMedia);
  stop.classList.add('danger');
  routeCleanup = () => {
    releaseMedia();
    reviewUrls.forEach((url) => URL.revokeObjectURL(url));
    preview.remove();
  };
}

function captureCrop(width: number, height: number, label: string): void {
  const output = document.createElement('canvas');
  output.width = width;
  output.height = height;
  const context = output.getContext('2d')!;
  const sourceRatio = canvas.width / canvas.height;
  const targetRatio = width / height;
  let sourceWidth = canvas.width;
  let sourceHeight = canvas.height;
  if (sourceRatio > targetRatio) sourceWidth = canvas.height * targetRatio;
  else sourceHeight = canvas.width / targetRatio;
  const sourceX = (canvas.width - sourceWidth) / 2;
  const sourceY = (canvas.height - sourceHeight) / 2;
  context.drawImage(canvas, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height);
  output.toBlob((blob) => { if (blob) downloadBlob(blob, `buncha-coasters-${label}.png`); }, 'image/png');
}

function configureHero(): void {
  addGround();
  addTrack();
  addScenery();
  addCar();
  simulation.distance = track.length * 0.39;
  placeCar();
  camera.position.set(38, 30, 40);
  camera.lookAt(0, 7, 2);
  const formats: [number, number, string][] = [[1200, 675, 'card-16x9'], [1200, 900, 'hero-4x3'], [1200, 628, 'open-graph'], [1080, 1920, 'social-9x16']];
  formats.forEach(([width, height, label]) => button(`${label} · ${width}×${height}`, () => captureCrop(width, height, label)));
  setStatus('Camera, scene seed, and center-crop rule are fixed. Repeated captures should be pixel-equivalent on the same renderer.');
}

function activateRoute(): void {
  const candidate = location.hash.slice(1) as Route;
  activeRoute = candidate in ROUTES ? candidate : 'theme-proof';
  if (candidate !== activeRoute) history.replaceState(null, '', `#${activeRoute}`);
  clearContent();
  const info = ROUTES[activeRoute];
  title.textContent = info.title;
  description.textContent = info.description;
  checks.innerHTML = info.checks.map((item) => `<li>${item}</li>`).join('');
  nav.querySelectorAll('button').forEach((element) => element.classList.toggle('active', element.dataset.route === activeRoute));
  switch (activeRoute) {
    case 'theme-proof': configureThemeProof(); break;
    case 'geometry': configureGeometry(); break;
    case 'physics': configurePhysics(); break;
    case 'editor': configureEditor(); break;
    case 'ride-perf': configurePhysics(true); break;
    case 'media': configureMedia(); break;
    case 'camera': configureCamera(); break;
    case 'hero': configureHero(); break;
  }
  button('Export metrics JSON', () => downloadJson(metrics.snapshot(activeRoute, renderer, fixtures, themeEvidence(THEMES[activeTheme])), `buncha-coasters-${activeRoute}-${activeTheme}-metrics.json`));
}

function resize(): void {
  const parent = canvas.parentElement!;
  const width = parent.clientWidth;
  const height = parent.clientHeight;
  renderer.setSize(width, height, false);
  camera.aspect = width / Math.max(1, height);
  camera.updateProjectionMatrix();
}

function renderMetrics(now: number): void {
  if (now - lastMetricsRender < 500) return;
  lastMetricsRender = now;
  const data = metrics.snapshot(activeRoute, renderer, fixtures, themeEvidence(THEMES[activeTheme]));
  metricsBox.innerHTML = [
    ['Environment', data.theme.label],
    ['Approx FPS', data.render.fpsApprox],
    ['Frame p50 / p95', `${data.render.frameP50Ms} / ${data.render.frameP95Ms} ms`],
    ['Draw calls', data.render.drawCalls],
    ['Triangles', data.render.triangles],
    ['Geometries / textures', `${data.render.geometries} / ${data.render.textures}`],
    ['JS heap', data.environment.heapUsedMB === null ? 'unavailable' : `${data.environment.heapUsedMB} MB`],
    ['Rebuild', `${rebuildLatency.toFixed(2)} ms`],
  ].map(([label, value]) => `<div class="metric"><span>${label}</span><strong>${value}</strong></div>`).join('');
}

let previous = performance.now();
function animate(now: number): void {
  const delta = Math.min(0.05, (now - previous) / 1000);
  previous = now;
  elapsed += delta;
  metrics.frame(now);
  if (['physics', 'ride-perf', 'media', 'camera'].includes(activeRoute)) {
    accumulator += delta;
    while (accumulator >= 1 / 120) {
      simulation = simulateStep(track, simulation);
      accumulator -= 1 / 120;
    }
    const frame = frameAtDistance(track, simulation.distance);
    placeCar(frame);
    if (activeRoute === 'ride-perf') {
      camera.position.copy(frame.point).addScaledVector(frame.normal, 3.1).addScaledVector(frame.tangent, -7.5);
      camera.lookAt(frame.point.clone().addScaledVector(frame.tangent, 6));
    }
    hud.textContent = `${(simulation.speed * 2.23694).toFixed(1)} mph\n${simulation.verticalG.toFixed(2)} vertical G\n${simulation.lateralG.toFixed(2)} lateral G\npeak ${simulation.peakSpeed.toFixed(2)} m/s`;
  } else {
    hud.textContent = activeRoute === 'editor' ? 'Drag cyan control points' : `${track.length.toFixed(1)} m sampled track\n${track.frames.length} orientation frames`;
  }
  if (activeRoute === 'geometry') {
    orbitAngle += delta * 0.08;
    camera.position.x = Math.cos(orbitAngle) * 52;
    camera.position.z = Math.sin(orbitAngle) * 52;
    camera.lookAt(0, 7, 2);
  }
  renderer.render(scene, camera);
  renderMetrics(now);
  requestAnimationFrame(animate);
}

themePicker.addEventListener('change', () => {
  const candidate = themePicker.value as ThemeId;
  if (!(candidate in THEMES)) return;
  activeTheme = candidate;
  applyActiveTheme();
  updateThemeProofCopy();
  setStatus(`${THEMES[activeTheme].label} applied to shared material slots and lighting. Geometry instances were not replaced.`);
});
addEventListener('hashchange', activateRoute);
addEventListener('resize', resize);
resize();
applyActiveTheme();
activateRoute();
requestAnimationFrame(animate);
